import React, { useState, useEffect } from 'react'
import { 
  CreditCard, DollarSign, TrendingUp, History,
  CheckCircle, AlertCircle, Info, Send, Calculator,
  Banknote, Users, Calendar, ChevronRight, Settings,
  Undo, Edit2, Trash2
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface Kid {
  id: string
  display_name: string
  username: string
  total_points: number
  available_points?: number
  converted_points?: number
  lifetime_points?: number
}

interface Conversion {
  id: string
  user_id: string
  username: string
  display_name: string
  points_converted: number
  dollar_amount: number
  conversion_rate: number
  payment_method: string
  notes?: string
  created_at: string
}

export const GreenlightConverter: React.FC = () => {
  const [kids, setKids] = useState<Kid[]>([])
  const [conversions, setConversions] = useState<Conversion[]>([])
  const [selectedKid, setSelectedKid] = useState<string>('')
  const [pointsToConvert, setPointsToConvert] = useState<number>(0)
  const [paymentMethod, setPaymentMethod] = useState<string>('greenlight')
  const [transactionRef, setTransactionRef] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [conversionRate, setConversionRate] = useState(0.10) // $0.10 per point (10 points = $1)
  const [editingRate, setEditingRate] = useState(false)
  const [newRate, setNewRate] = useState('10')
  const [conversionDate, setConversionDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'))

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [kidsData, conversionsData, settingsData] = await Promise.all([
        apiClient.get('/api/parent/children'),
        apiClient.get('/api/conversions/history').catch(() => []),
        apiClient.get('/api/exchange-rate').catch(() => ({ points_per_dollar: 10 }))
      ])

      // Defensive checks for Go backend wrapped response format
      const kidsArray = Array.isArray(kidsData) ? kidsData :
                        (kidsData?.children && Array.isArray(kidsData.children)) ? kidsData.children : []
      const conversionsArray = Array.isArray(conversionsData) ? conversionsData :
                               (conversionsData?.conversions && Array.isArray(conversionsData.conversions)) ? conversionsData.conversions : []

      // Use the available points from backend, or calculate if not present
      const kidsWithPoints = kidsArray.map((kid: Kid) => ({
        ...kid,
        available_points: kid.available_points ?? (kid.total_points || 0),
        converted_points: kid.converted_points ?? 0,
        lifetime_points: kid.lifetime_points ?? (kid.total_points || 0)
      }))
      
      setKids(kidsWithPoints)
      setConversions(conversionsArray)
      
      // Set conversion rate from settings (points_per_dollar = 10 means 10 points = $1)
      const pointsPerDollar = settingsData.points_per_dollar || 10
      setConversionRate(1 / pointsPerDollar)
      setNewRate(pointsPerDollar.toString())
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load kids data')
    }
  }
  
  const handleSaveRate = async () => {
    try {
      const pointsPerDollar = parseInt(newRate)
      if (isNaN(pointsPerDollar) || pointsPerDollar < 1) {
        toast.error('Invalid conversion rate')
        return
      }
      
      // The rate is the family's exchange_rate setting (there is no PUT on
      // /api/exchange-rate); the Rewards Store reads the same value.
      await apiClient.put('/api/system/settings', { exchange_rate: String(pointsPerDollar) })
      
      setConversionRate(1 / pointsPerDollar)
      setEditingRate(false)
      toast.success(`Conversion rate updated: ${pointsPerDollar} points = $1`)
    } catch (error) {
      toast.error('Failed to save conversion rate')
    }
  }

  const handleUndoConversion = async (conversionId: string) => {
    if (!confirm('Are you sure you want to undo this conversion? Points will be restored to the kid.')) {
      return
    }
    
    try {
      await apiClient.post(`/api/conversions/${conversionId}/undo`)
      toast.success('Conversion undone successfully')
      await loadData()
    } catch (error) {
      toast.error('Failed to undo conversion')
    }
  }

  const [editingNotes, setEditingNotes] = useState<string | null>(null)
  const [editNotesText, setEditNotesText] = useState<string>('')

  const handleUpdateNotes = async (conversionId: string) => {
    try {
      await apiClient.put(`/api/conversions/${conversionId}/notes`, {
        notes: editNotesText
      })
      toast.success('Notes updated')
      setEditingNotes(null)
      await loadData()
    } catch (error) {
      toast.error('Failed to update notes')
    }
  }

  const handleConvert = async () => {
    if (!selectedKid || !pointsToConvert) {
      toast.error('Please select a kid and enter points to convert')
      return
    }

    const kid = kids.find(k => k.id === selectedKid)
    if (!kid) return

    if (pointsToConvert > (kid.available_points || 0)) {
      toast.error(`${kid.display_name} only has ${kid.available_points} points available`)
      return
    }

    setLoading(true)
    try {
      const requestPayload = {
        user_id: selectedKid,
        points: pointsToConvert,
        payment_method: paymentMethod,
        transaction_ref: transactionRef,
        notes: notes,
        conversion_date: conversionDate
      }

      await apiClient.post('/api/conversions/convert', requestPayload)

      toast.success(
        <div>
          <p className="font-medium">Conversion successful!</p>
          <p className="text-sm">
            Converted {pointsToConvert} points to ${(pointsToConvert * conversionRate).toFixed(2)} for {kid.display_name}
          </p>
        </div>
      )

      // Reset form
      setPointsToConvert(0)
      setTransactionRef('')
      setNotes('')
      
      // Reload data
      await loadData()
    } catch (error: any) {
      console.error('Conversion error:', error.response?.data)
      console.error('Full error:', error)
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to convert points')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkConvert = async () => {
    if (!pointsToConvert) {
      toast.error('Please enter points to convert')
      return
    }

    setLoading(true)
    try {
      const conversionsToMake = kids
        .filter(kid => (kid.available_points || 0) >= pointsToConvert)
        .map(kid => ({
          user_id: kid.id,
          points: pointsToConvert,
          payment_method: paymentMethod,
          notes: `Bulk conversion - ${notes || 'Weekly allowance'}`
        }))

      if (conversionsToMake.length === 0) {
        toast.error('No kids have enough points for this conversion')
        return
      }

      // No bulk endpoint: convert one kid at a time and report what worked.
      let ok = 0
      const failed: string[] = []
      for (const c of conversionsToMake) {
        try {
          await apiClient.post('/api/conversions/convert', c)
          ok += 1
        } catch {
          failed.push(kids.find(k => k.id === c.user_id)?.display_name || 'a child')
        }
      }
      if (failed.length > 0) {
        toast.error(`Converted for ${ok} of ${conversionsToMake.length}. Failed: ${failed.join(', ')}`)
        await loadData()
        return
      }

      toast.success(`Converted ${pointsToConvert} points for ${conversionsToMake.length} kids`)
      
      // Reset and reload
      setPointsToConvert(0)
      setNotes('')
      await loadData()
    } catch (error) {
      toast.error('Failed to perform bulk conversion')
    } finally {
      setLoading(false)
    }
  }

  const selectedKidData = kids.find(k => k.id === selectedKid)
  const dollarAmount = pointsToConvert * conversionRate

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Greenlight Points Converter</h2>
            <p className="text-gray-600 mt-1">Convert earned points to real money</p>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end space-x-2">
              <div>
                <p className="text-sm text-gray-500">Conversion Rate</p>
                <p className="text-lg font-bold text-green-600">
                  {Math.round(1/conversionRate)} points = $1.00
                </p>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Configure conversion rate"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Available</p>
                <p className="text-2xl font-bold text-blue-900">
                  {kids.reduce((sum, k) => sum + (k.available_points || 0), 0)}
                </p>
              </div>
              <Calculator className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Value</p>
                <p className="text-2xl font-bold text-green-900">
                  ${(kids.reduce((sum, k) => sum + (k.available_points || 0), 0) * conversionRate).toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">This Week</p>
                <p className="text-2xl font-bold text-purple-900">
                  {conversions.filter(c => {
                    const date = new Date(c.created_at)
                    const weekAgo = new Date()
                    weekAgo.setDate(weekAgo.getDate() - 7)
                    return date > weekAgo
                  }).length}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Kids Ready</p>
                <p className="text-2xl font-bold text-orange-900">
                  {kids.filter(k => (k.available_points || 0) >= 100).length}
                </p>
              </div>
              <Users className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Conversion Rate Settings</h3>
          
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points per Dollar
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  disabled={!editingRate}
                  className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  min="1"
                  step="1"
                />
                <span className="text-gray-600">points = $1.00</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Current: {Math.round(1/conversionRate)} points = $1.00 (${conversionRate.toFixed(2)} per point)
              </p>
            </div>
            
            <div className="flex space-x-2">
              {editingRate ? (
                <>
                  <button
                    onClick={handleSaveRate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingRate(false)
                      setNewRate(Math.round(1/conversionRate).toString())
                    }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditingRate(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit Rate
                </button>
              )}
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Important:</p>
                <p>Changing the conversion rate only affects future conversions. Past conversions will keep their original rate.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conversion Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Convert Points</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Kid
              </label>
              <select
                value={selectedKid}
                onChange={(e) => setSelectedKid(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a kid...</option>
                {kids.map(kid => (
                  <option key={kid.id} value={kid.id}>
                    {kid.display_name} - {kid.available_points || 0} points available
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points to Convert
              </label>
              <input
                type="number"
                value={pointsToConvert || ''}
                onChange={(e) => setPointsToConvert(parseInt(e.target.value) || 0)}
                placeholder="Enter points..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {selectedKidData && pointsToConvert > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  = ${dollarAmount.toFixed(2)} • Remaining: {(selectedKidData.available_points || 0) - pointsToConvert} points
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="greenlight">Greenlight Card</option>
                <option value="cash">Cash</option>
                <option value="venmo">Venmo</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conversion Date
              </label>
              <input
                type="date"
                value={conversionDate}
                onChange={(e) => setConversionDate(e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">For backdating transactions</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Reference (Optional)
              </label>
              <input
                type="text"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                placeholder="e.g., GL-12345"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Weekly allowance, Birthday bonus..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Conversion Summary */}
            {selectedKidData && pointsToConvert > 0 && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Conversion Summary</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Converting {pointsToConvert} points to ${dollarAmount.toFixed(2)} for {selectedKidData.display_name}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedKidData.display_name} will be notified immediately
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-6 pt-6 border-t">
          <div className="flex space-x-2">
            <button
              onClick={handleBulkConvert}
              disabled={loading || !pointsToConvert}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Bulk Convert All Kids
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              <History className="w-4 h-4 inline mr-2" />
              History
            </button>
          </div>

          <button
            onClick={handleConvert}
            disabled={loading || !selectedKid || !pointsToConvert}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {loading ? (
              <span>Converting...</span>
            ) : (
              <>
                <Send className="w-4 h-4 inline mr-2" />
                Convert Points
              </>
            )}
          </button>
        </div>
      </div>

      {/* Recent Conversions */}
      {showHistory && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Conversions</h3>
          
          {conversions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No conversions yet</p>
          ) : (
            <div className="space-y-2">
              {conversions.slice(0, 20).map(conversion => (
                <div key={conversion.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {conversion.display_name || conversion.username}
                        </p>
                        <p className="text-sm text-gray-500">
                          {conversion.points_converted} points → ${Number(conversion.dollar_amount).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {format(new Date(conversion.created_at), 'MMM d, h:mm a')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {conversion.payment_method}
                        </p>
                      </div>
                      <button
                        onClick={() => handleUndoConversion(conversion.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Undo conversion"
                      >
                        <Undo className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* Notes section */}
                  <div className="mt-2 ml-8">
                    {editingNotes === conversion.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editNotesText}
                          onChange={(e) => setEditNotesText(e.target.value)}
                          placeholder="Add a note..."
                          className="flex-1 px-2 py-1 border rounded text-sm"
                        />
                        <button
                          onClick={() => handleUpdateNotes(conversion.id)}
                          className="text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingNotes(null)}
                          className="text-gray-600 hover:text-gray-700 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingNotes(conversion.id)
                          setEditNotesText(conversion.notes || '')
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        {conversion.notes || 'Add note...'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}